import { analyticsText } from "../../services/analytics";
import type { RecommenderAnswer } from "./dynamic-recommender-api";

/**
 * The values the Dynamic recommender reports (WAT-12366). Every control reports
 * one position; the status events carry their categories on `dim_name` and the
 * matching values on `dim_value`, `|` between categories and `~` within one.
 */
export const RECOMMENDER_LINK_POSITION = "product recommender";
export const RECOMMENDER_SEARCH_CATEGORY = "search component";
export const RECOMMENDER_SEARCH_SOURCE = "search-product recommender";
export const UPDATE_LOCATION_LINK_NAME = "update location";
export const CLEAR_ALL_LINK_NAME = "clear all";
export const RESET_SEARCH_LINK_NAME = "reset search";

/** Constant per slot: both cards' button reads the same, so the slot tells them apart. */
const CARD_LINK_LABELS = ["top recommendation", "alternative recommendation"] as const;

export interface RecommenderDimensions {
  dimName: string;
  dimValue: string;
}

export function interestLinkName(label: string, selected: boolean): string {
  return `${analyticsText(label)}_${selected ? "selected" : "unselected"}`;
}

export const searchLinkName = (channelName: string) => `search - ${analyticsText(channelName)}`;

export const removeLinkName = (channelName: string) => `remove ${analyticsText(channelName)}`;

export function cardLinkName(slotIndex: number, packageName: string): string {
  return `${CARD_LINK_LABELS[slotIndex === 0 ? 0 : 1]} - ${analyticsText(packageName)}`;
}

/** Only the categories with a value are reported; with neither, `selections` stands in for both. */
export function selectionStatus(interests: string[], channels: string[], zip: string): RecommenderDimensions {
  const categories = ([["interests", interests], ["channels", channels]] as const)
    .map(([name, values]) => [name, values.map(analyticsText).filter(Boolean)] as const)
    .filter(([, values]) => values.length > 0);
  if (categories.length === 0) {
    return {
      dimName: "selections | zip",
      dimValue: `no selections | ${zip}`
    };
  }
  return {
    dimName: [...categories.map(([name]) => name), "zip"].join(" | "),
    dimValue: [...categories.map(([, values]) => values.join("~")), zip].join(" | ")
  };
}

/** The `displayed` push, then the card count; none for an answer without cards. A blank code reports as "unknown", as the quiz's does. */
export function recommendationStatus(cards: RecommenderAnswer["cards"]): RecommenderDimensions[] {
  if (!cards) {
    return [];
  }
  const shown = [cards.top, cards.alternative].filter(Boolean);
  if (shown.length === 0) {
    return [];
  }
  return [
    {
      dimName: "displayed",
      dimValue: shown.map(card => card.code || "unknown").join("|")
    },
    shown.length === 1 ? {
      dimName: "card count",
      dimValue: "1"
    } : {
      dimName: "card count | card 2 type",
      dimValue: `2 | ${cards.alternativeKind || "unknown"}`
    }
  ];
}

/** Where a clicked link lands: its href as the browser resolved it, so a relative one reports in full. */
export function clickedHref({ currentTarget }: { currentTarget: EventTarget | null }): string {
  return currentTarget instanceof HTMLAnchorElement ? currentTarget.href : "";
}

/** The channel line-up modal's reported destination: this page's path, then "channel lineup". */
export function channelLineupDestination(): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname.replace(/\/+$/, "")}/channel lineup`;
}
